/**
 * UploadZone.tsx — 2004va
 * Accepts photos, videos, audio, and documents (PDF, Word, text, stories).
 * All media types are first-class — The Room routes everything.
 */
import { useRef, useState, type DragEvent } from 'react'
import { Upload, Film, Music, FileText, Image } from 'lucide-react'
import { cn } from '@/components/ui/utils'

// All accepted types — photos, video, audio, documents, stories
const ACCEPTED = [
  'image/*',
  'video/*',
  'audio/*',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
].join(',')

const MAX_SIZE = 500 * 1024 * 1024  // 500MB — covers long-form video

const TYPE_GROUPS = [
  { icon: <Image   size={13} />, label: 'Photo'   },
  { icon: <Film    size={13} />, label: 'Video'   },
  { icon: <Music   size={13} />, label: 'Audio'   },
  { icon: <FileText size={13} />, label: 'Story / PDF' },
]

interface Props {
  onFiles:  (files: File[]) => void
  disabled?: boolean
}

export function UploadZone({ onFiles, disabled }: Props) {
  const inputRef        = useRef<HTMLInputElement>(null)
  const [drag, setDrag] = useState(false)
  const [err,  setErr]  = useState<string | null>(null)

  function validate(files: FileList | null): File[] {
    setErr(null)
    if (!files || files.length === 0) return []
    const valid:  File[]   = []
    const errors: string[] = []
    Array.from(files).forEach(f => {
      if (f.size > MAX_SIZE) errors.push(`${f.name} is too large (max 500MB)`)
      else valid.push(f)
    })
    if (errors.length) setErr(errors.join(' · '))
    return valid
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = validate(e.target.files)
    if (files.length) onFiles(files)
    if (inputRef.current) inputRef.current.value = ''
  }

  function onDrop(e: DragEvent) {
    e.preventDefault()
    setDrag(false)
    const files = validate(e.dataTransfer.files)
    if (files.length) onFiles(files)
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        multiple
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />

      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); if (!disabled) setDrag(true) }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={cn(
          'relative cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all duration-300 select-none',
          drag
            ? 'border-room-accent bg-room-accent/10 animate-glow'
            : 'border-room-border bg-room-surface hover:border-room-muted hover:bg-room-surface/80',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Upload
          className={cn('mx-auto mb-4 transition-colors', drag ? 'text-room-accent' : 'text-room-muted')}
          size={44}
          strokeWidth={1.5}
        />
        <p className="text-lg font-bold text-white mb-1">
          {drag ? 'Drop to enter The Room' : 'Tap to upload'}
        </p>
        <p className="text-sm text-room-muted mb-4">
          Photos · Video · Audio · Stories · PDFs
        </p>

        <div className="flex items-center justify-center gap-4 text-room-muted">
          {TYPE_GROUPS.map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-1 text-xs">
              {icon} {label}
            </div>
          ))}
        </div>
      </div>

      {err && <p className="text-xs text-room-error px-1">{err}</p>}
    </div>
  )
}
